/**
 * useForensicOverlayStore — per-case forensic record overlay.
 *
 * The backend groups verified_tx_links + forensic_records for a case into a
 * graph-ready `{nodes, edges, records}` payload. We cache it here keyed by
 * case_id. Any investigation under that case reads from the same cached
 * overlay — consistent with our "uploads tied to case, not investigation"
 * promise.
 *
 * Used by:
 *   - Workbench load effect: fetch + merge forensic-only nodes to canvas
 *   - GraphCanvas: decorate any node whose address is in the overlay
 *   - NodeInspector ForensicTab: show KYC + record list for the selected node
 */
import { create } from 'zustand';

import {
    forensicService,
    type ForensicOverlay,
    type ForensicOverlayNode,
    type ForensicOverlayEdge,
    type ForensicOverlayRecord,
} from '../services/api';


/** Stable lookup key. Chain preserved as-is (EVM already lowercased upstream). */
export function overlayKey(chain: string | null | undefined, address: string | null | undefined): string {
    return `${(chain || '').toUpperCase()}|${address || ''}`;
}

interface State {
    /** caseId → overlay (null while loading, undefined if not yet fetched) */
    byCase: Record<string, ForensicOverlay | null>;
    /** Per-case loading flag so components can show a spinner if they care. */
    loading: Record<string, boolean>;
    /** Per-case error, if fetch failed. */
    error: Record<string, string | null>;
}

interface Actions {
    fetch: (caseId: string) => Promise<ForensicOverlay | null>;
    clear: (caseId?: string) => void;
    /** Get the overlay for the active case. Does NOT auto-fetch. */
    get: (caseId: string) => ForensicOverlay | null;
    /** Lookup a node by (chain, address). Returns undefined if not in overlay. */
    getNode: (caseId: string, chain: string, address: string) => ForensicOverlayNode | undefined;
    /** Lookup records referenced by a node. */
    getRecordsForNode: (caseId: string, node: ForensicOverlayNode) => ForensicOverlayRecord[];
    /** Lookup edges touching a node. */
    getEdgesForNode: (caseId: string, chain: string, address: string) => ForensicOverlayEdge[];
}

export const useForensicOverlayStore = create<State & Actions>((set, get) => ({
    byCase: {},
    loading: {},
    error: {},

    fetch: async (caseId) => {
        set(s => ({ loading: { ...s.loading, [caseId]: true }, error: { ...s.error, [caseId]: null } }));
        try {
            const overlay = await forensicService.getCanvasOverlay(caseId);
            set(s => ({
                byCase: { ...s.byCase, [caseId]: overlay },
                loading: { ...s.loading, [caseId]: false },
            }));
            return overlay;
        } catch (e: any) {
            const msg = e?.response?.data?.detail || e?.message || 'fetch failed';
            set(s => ({
                byCase: { ...s.byCase, [caseId]: null },
                loading: { ...s.loading, [caseId]: false },
                error: { ...s.error, [caseId]: msg },
            }));
            return null;
        }
    },

    clear: (caseId) => {
        if (caseId) {
            set(s => {
                const { [caseId]: _, ...rest } = s.byCase;
                return { byCase: rest };
            });
        } else {
            set({ byCase: {}, loading: {}, error: {} });
        }
    },

    get: (caseId) => get().byCase[caseId] ?? null,

    getNode: (caseId, chain, address) => {
        const overlay = get().byCase[caseId];
        if (!overlay) return undefined;
        const key = overlayKey(chain, address);
        return overlay.nodes.find(n => overlayKey(n.chain, n.address) === key);
    },

    getRecordsForNode: (caseId, node) => {
        const overlay = get().byCase[caseId];
        if (!overlay) return [];
        return overlay.records.filter(r => node.record_ids.includes(r.id));
    },

    getEdgesForNode: (caseId, chain, address) => {
        const overlay = get().byCase[caseId];
        if (!overlay) return [];
        const key = overlayKey(chain, address);
        return overlay.edges.filter(e =>
            overlayKey(e.target_chain, e.target_address) === key ||
            (e.source_address && overlayKey(e.source_chain, e.source_address) === key)
        );
    },
}));
